import { useState, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Clock, CheckCircle, X, MessageSquare } from 'lucide-react';
import { API_URL } from '@/services/apiClient';
import { safeLog } from '@/utils/safeLog';

interface PendingCorrection {
  correctionId: string;
  originalContent: string;
  detectionType: string;
  severity: string;
  suggestedMessage: string;
  expiresAt: string;
  remainingMinutes: number;
}

export function SelfCorrectionBanner() {
  const [corrections, setCorrections] = useState<PendingCorrection[]>([]);
  const [correcting, setCorrecting]   = useState<string | null>(null);
  const [dismissed, setDismissed]     = useState<Set<string>>(new Set());
  const [breathing, setBreathing]     = useState(false);

  useEffect(() => {
    fetchCorrections();
    const interval = setInterval(fetchCorrections, 30000);

    // KAN-13: Immediately refresh when a self-correction push arrives in the foreground
    const handlePush = () => fetchCorrections();
    window.addEventListener('self-correction-push', handlePush);

    return () => {
      clearInterval(interval);
      window.removeEventListener('self-correction-push', handlePush);
    };
  }, []);

  const fetchCorrections = async () => {
    try {
      const token = sessionStorage.getItem('authToken');
      const res = await fetch(`${API_URL}/student/pending-corrections`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setCorrections(data.corrections || []);
      }
    } catch (err) {
      safeLog.error('Failed to fetch corrections:', err);
    }
  };

  const handleSelfCorrect = async (correctionId: string) => {
    try {
      setCorrecting(correctionId);
      const token = sessionStorage.getItem('authToken');
      const res = await fetch(`${API_URL}/student/self-correct`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ correctionId })
      });

      if (res.ok) {
        // Show breathing / mindfulness card for 5 s
        setBreathing(true);
        setTimeout(() => setBreathing(false), 5000);
        setCorrections(prev => prev.filter(c => c.correctionId !== correctionId));

        // KAN-13: Dismiss the push notification for this correction
        if ('serviceWorker' in navigator) {
          navigator.serviceWorker.ready.then(reg => {
            reg.getNotifications({ tag: `self-correction-${correctionId}` })
              .then(notifications => notifications.forEach(n => n.close()));
          });
        }
      }
    } catch (err) {
      safeLog.error('Failed to self-correct:', err);
    } finally {
      setCorrecting(null);
    }
  };

  const visibleCorrections = corrections.filter(c => !dismissed.has(c.correctionId));

  if (visibleCorrections.length === 0 && !breathing) return null;

  return (
    <div className="space-y-3">
      {/* ── Breathing / mindfulness card (shown for 5 s after a successful rephrasing) ── */}
      {breathing && (
        <div className="bg-emerald-50 border-2 border-emerald-200 rounded-2xl p-5 text-center">
          <p className="text-3xl mb-2">🌿</p>
          <p className="text-emerald-800 font-semibold text-sm">Great choice!</p>
          <p className="text-emerald-600 text-xs mt-1 leading-relaxed">
            Take a slow, deep breath. You just made a positive decision. +10 points earned!
          </p>
        </div>
      )}

      {visibleCorrections.map((correction) => {
        const severityColor = correction.severity === 'high'
          ? 'border-red-400 bg-red-50'
          : correction.severity === 'medium'
          ? 'border-orange-400 bg-orange-50'
          : 'border-yellow-400 bg-yellow-50';

        const severityIcon = correction.severity === 'high'
          ? 'text-red-500'
          : correction.severity === 'medium'
          ? 'text-orange-500'
          : 'text-yellow-500';

        return (
          <Card key={correction.correctionId} className={`border-2 ${severityColor} animate-in slide-in-from-top`}>
            <CardContent className="p-4">
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-start gap-3 flex-1">
                  <MessageSquare className={`h-5 w-5 mt-0.5 flex-shrink-0 ${severityIcon}`} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <p className="text-sm font-semibold text-gray-800">
                        We noticed something in your message
                      </p>
                      <span className="flex items-center gap-1 text-xs text-gray-500">
                        <Clock className="h-3 w-3" />
                        {correction.remainingMinutes}m left
                      </span>
                    </div>

                    <p className="text-xs text-gray-500 mb-2">
                      It may contain content related to{' '}
                      <span className="font-medium">{correction.detectionType.replace(/_/g, ' ')}</span>.
                      {' '}Want to rephrase before it's sent?
                    </p>

                    <div className="bg-white rounded-lg p-2.5 border border-green-200 mb-3">
                      <div className="flex items-start gap-2">
                        <MessageSquare className="h-4 w-4 text-green-600 mt-0.5 flex-shrink-0" />
                        <div>
                          <p className="text-xs font-medium text-green-700 mb-0.5">Try saying this instead:</p>
                          <p className="text-sm text-gray-700">"{correction.suggestedMessage}"</p>
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center gap-2">
                      <Button
                        size="sm"
                        onClick={() => handleSelfCorrect(correction.correctionId)}
                        disabled={correcting === correction.correctionId}
                        className="bg-green-600 hover:bg-green-700"
                      >
                        {correcting === correction.correctionId ? (
                          <span className="flex items-center gap-1">
                            <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white" />
                            Rephrasing...
                          </span>
                        ) : (
                          <span className="flex items-center gap-1">
                            <CheckCircle className="h-3.5 w-3.5" />
                            Rephrase It · +10 pts
                          </span>
                        )}
                      </Button>
                    </div>

                    <p className="text-xs text-gray-400 mt-2">
                      You have {correction.remainingMinutes} minute{correction.remainingMinutes !== 1 ? 's' : ''} to rephrase — taking a moment can make a real difference.
                    </p>
                  </div>
                </div>

                <button
                  onClick={() => setDismissed(prev => new Set([...prev, correction.correctionId]))}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}
